/**
 * Format identity and the three version numbers that mean different things.
 *
 * They are separate on purpose. A reader needs to answer "can I open this file
 * at all" (formatVersion), "do these numbers mean what I think" (semantics),
 * and "are these derived statistics current" (analytics) independently — and
 * conflating any two of them makes one of the questions unanswerable.
 */
/** Identity marker. Present in every file, never changes across versions. */
export const STRAT_FORMAT = "strat";
/**
 * Bumped only for a change an older reader would *misinterpret*. Adding an
 * optional field is not such a change — readers ignore what they don't know —
 * so new optional fields ship without a bump.
 *
 * A reader seeing a higher version must refuse the file and say so. It cannot
 * know what changed, and guessing is how a format loses the ability to make
 * any promise at all.
 *
 * **2** added three things a version-1 reader would silently get wrong: an
 * operand's `offset` (it would read the current bar), an indicator's
 * `timeframe` (it would compute on the run's own chart) and the `formula`
 * kind. This is the highest version this build reads. It is *not* stamped on
 * every file: the writer stamps the lowest version that can express what the
 * file holds (`formatVersionFor`), so a strategy that uses none of the three
 * still opens in a build that only knows version 1.
 */
export const STRAT_FORMAT_VERSION = 2;
/** Every file version 1 could express is still written as version 1. */
export const STRAT_FORMAT_VERSION_BASE = 1;
/**
 * File suffixes the writer emits, chosen by what the file holds (see
 * `fileSuffixFor` and docs/adr/0011): rules alone are a `.strategy`, anything
 * with a finished run in it is a `.backtest`, and a gzipped backtest is a
 * `.backtest.gz`. The contents are the same JSON envelope in all three.
 */
export const STRATEGY_FILE_SUFFIX = ".strategy";
export const BACKTEST_FILE_SUFFIX = ".backtest";
export const GZIP_SUFFIX = ".gz";
export const BACKTEST_GZ_FILE_SUFFIX = `${BACKTEST_FILE_SUFFIX}${GZIP_SUFFIX}`;
/** What every export wrote before ADR 0011. Still read, never written. */
export const LEGACY_STRAT_FILE_SUFFIX = ".strat";
/**
 * Every suffix a reader will open. The legacy `.strat` stays so every file
 * already on someone's disk still opens, and `.json` so a file a browser saved
 * with an extra extension does too.
 *
 * None of these are load-bearing: identity comes from the envelope's `format`
 * field, which is checked before anything else is inspected, and gzip is
 * recognised by its first two bytes rather than its name. The extension is a
 * convenience for file pickers, drop zones and the desktop's file associations.
 */
export const STRAT_READABLE_SUFFIXES = [
    STRATEGY_FILE_SUFFIX,
    BACKTEST_FILE_SUFFIX,
    BACKTEST_GZ_FILE_SUFFIX,
    LEGACY_STRAT_FILE_SUFFIX,
    ".strat.json",
    ".json",
];
/**
 * Registered media type. The `+json` structured suffix (RFC 6839) is what tells
 * a generic tool the payload is JSON even though the type name does not say so.
 */
export const STRAT_MEDIA_TYPE = "application/vnd.strat+json";
/** A `.backtest.gz` is plain gzip around the same JSON. */
export const GZIP_MEDIA_TYPE = "application/gzip";
/** Stamped into `generator.version`. Kept in step with the root package.json. */
export const STRAT_APP_VERSION = "0.1.0";
/**
 * Bumped when a change moves the numbers a *correct* strategy produces.
 *
 * This is not a version of the code — a behaviour-preserving refactor, however
 * large, does not touch it. It is a version of the *answers*, so that a result
 * can say which rules produced it and two results can be compared honestly.
 *
 * Version 1 **is** the corrected engine, not a predecessor of it. Greenfield
 * means there is no installed base of results produced under the old rules, so
 * the merged semantics — next-open signal exits, sizing against what is
 * affordable, pnlPct as a fraction of margin, UTC daily buckets, the warm-up
 * gate on bars elapsed — are simply what version 1 says. The corrections are
 * recorded in docs/SEMANTICS.md against the *prototypes*, which is where they
 * were made; nothing in this repository ever computed a number the other way.
 */
export const SEMANTICS_VERSION = 1;
//# sourceMappingURL=version.js.map